import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
} from "react";
import { Sparkles, Star } from "lucide-react";

/*
押した所から広がる波紋と、まわりに散る印 (一部は金)。適職診断の選択肢の札で作った動きを、
診断から切り離した共有の部品にしたもの。形と動きは styles/press-burst.css。減らす設定では CSS が両方を消す。

置き方は 2 通り。
- 部品が自分の中身を持つ札に置く  usePressBurst() の fire を onClick に、layer を札の中に置く (札は position が static 以外)
- 状態を呼び手が持つ  PressBurstLayer に at を渡す。at が変わるたび (n が変わるたび) に出し直す

出した動きは PRESS_BURST_MS のあと自分で消える。押した処理そのものは動きに頼らない (減らす設定でも通る)。

黄 (金) は gold で決める。1 つの場面で黄は 1 つという決まりのため、札の地や釦がもう黄の所は "none" にする
*/

export type PressBurstOrigin = {
  /** 札の左上からの px。キーボードで押したときは null (札の中ほどから出す) */
  x: number | null;
  y: number | null;
  /** 押した回数。変わるたびに動きを出し直す */
  n: number;
};

/** "all" は診断と同じ (4 つが金)。"none" は印が全部緑 (同じ場面にもう黄があるとき) */
export type PressBurstGold = "all" | "none";

/** 動きの長さ (CSS の 260〜270ms と、印の遅れ 30ms) より少し長く置いて消す */
export const PRESS_BURST_MS = 320;

/** 札のまわりに散らす印の置き場所 (札の幅と高さに対する割合) と、外へ動く向き (px)。数は 7 */
const SPARKS: readonly {
  left: number;
  top: number;
  dx: number;
  dy: number;
  gold: boolean;
  star: boolean;
  delay: number;
}[] = [
  { left: 6, top: 0, dx: -10, dy: -14, gold: true, star: false, delay: 0 },
  { left: 30, top: 0, dx: -4, dy: -16, gold: false, star: true, delay: 20 },
  { left: 62, top: 0, dx: 6, dy: -16, gold: true, star: true, delay: 10 },
  { left: 94, top: 4, dx: 14, dy: -12, gold: false, star: false, delay: 30 },
  { left: 98, top: 80, dx: 16, dy: 10, gold: true, star: false, delay: 0 },
  { left: 70, top: 100, dx: 8, dy: 16, gold: false, star: true, delay: 20 },
  { left: 18, top: 100, dx: -8, dy: 16, gold: true, star: true, delay: 10 },
];

function Burst({ at, gold }: { at: PressBurstOrigin; gold: PressBurstGold }) {
  const [alive, setAlive] = useState(true);
  useEffect(() => {
    const id = window.setTimeout(() => setAlive(false), PRESS_BURST_MS);
    return () => window.clearTimeout(id);
  }, []);
  if (!alive) return null;
  // 金を使わない場面 (学習の画面) は、暗い版で緑を明るくする (診断の暗い版は今のまま)
  const calm = gold === "none" ? " is-calm" : "";
  return (
    <>
      <span className="press-burst-clip" aria-hidden="true" data-testid="press-burst">
        <i
          className={`press-burst-ring${calm}`}
          style={at.x === null || at.y === null ? { left: "50%", top: "50%" } : { left: at.x, top: at.y }}
        />
      </span>
      {SPARKS.map((sp, k) => {
        const Icon = sp.star ? Star : Sparkles;
        return (
          <span
            key={k}
            className={`press-burst-spark${sp.gold && gold === "all" ? " is-gold" : ""}${calm}`}
            aria-hidden="true"
            style={
              {
                left: `${sp.left}%`,
                top: `${sp.top}%`,
                "--bx": `${sp.dx}px`,
                "--by": `${sp.dy}px`,
                animationDelay: `${sp.delay}ms`,
              } as CSSProperties
            }
          >
            <Icon size={18} strokeWidth={2.4} fill="currentColor" />
          </span>
        );
      })}
    </>
  );
}

/** 押した札の波紋と散る印。at が null なら何も出さない。at.n が変わるたびに出し直し、PRESS_BURST_MS で自分で消える */
export function PressBurstLayer({ at, gold = "all" }: { at: PressBurstOrigin | null; gold?: PressBurstGold }) {
  return at ? <Burst key={at.n} at={at} gold={gold} /> : null;
}

/**
 * 札の中に動きを置く手。fire を onClick で呼び、layer を札の中 (position が static 以外) に置く。
 * detail が 0 のクリックは Enter や Space で位置が無いので、札の中ほどから出す
 */
export function usePressBurst(gold: PressBurstGold = "all"): {
  fire: (e: MouseEvent<HTMLElement>) => void;
  layer: ReactNode;
} {
  const [at, setAt] = useState<PressBurstOrigin | null>(null);
  const count = useRef(0);
  const fire = useCallback((e: MouseEvent<HTMLElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    count.current += 1;
    setAt({
      x: e.detail === 0 ? null : e.clientX - r.left,
      y: e.detail === 0 ? null : e.clientY - r.top,
      n: count.current,
    });
  }, []);
  return { fire, layer: <PressBurstLayer at={at} gold={gold} /> };
}

/**
 * 中に置いた釦 (自分では動きを持たない部品) を包んで、押した所から動きを出す。
 * 押せない釦はクリックが出ないので動きも出ない。包みは inline-flex で、角は --ui-r-ctl (釦と同じ 8px)
 */
export function PressBurstHost({ children, gold = "all", className }: { children: ReactNode; gold?: PressBurstGold; className?: string }) {
  const { fire, layer } = usePressBurst(gold);
  return (
    <span className={`press-burst-host${className ? ` ${className}` : ""}`} onClickCapture={fire}>
      {children}
      {layer}
    </span>
  );
}
